import React, { useState, useEffect } from 'react';

const ThanksgivingSceneOverlay = () => {
  const [fallingLeaves, setFallingLeaves] = useState([]);

  useEffect(() => {
    // Generate warm autumn leaves
    const generateLeaves = () => {
      const leaves = [];
      for (let i = 0; i < 35; i++) {
        const depth = Math.random();
        leaves.push({
          id: i,
          left: Math.random() * 100,
          animationDuration: 15 + Math.random() * 20,
          animationDelay: Math.random() * 10,
          size: 8 + depth * 14,
          opacity: 0.4 + depth * 0.5,
          rotation: Math.random() * 360,
          leafType: Math.floor(Math.random() * 4),
          blur: (1 - depth) * 0.8,
        });
      }
      setFallingLeaves(leaves);
    };
    
    generateLeaves();
  }, []);

  return (
    <div className="thanksgiving-harvest-overlay">
      <style jsx>{`
        .thanksgiving-harvest-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
          pointer-events: none;
          overflow: hidden;
        }

        /* Warm golden autumn sky */
        .warm-autumn-sky {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            to bottom,
            #D4A574 0%,
            #E0B485 20%,
            #ECC896 40%,
            #F5D6A8 60%,
            #E8C89C 80%,
            #D4B488 100%
          );
          z-index: 0;
          opacity: 1;
        }

        /* Warm sunset glow */
        .golden-glow {
          position: absolute;
          top: 20%;
          left: 50%;
          transform: translateX(-50%);
          width: 80%;
          height: 50%;
          background: radial-gradient(
            ellipse at center,
            rgba(255, 220, 150, 0.6) 0%,
            rgba(255, 200, 120, 0.4) 30%,
            rgba(255, 180, 100, 0.2) 60%,
            transparent 80%
          );
          z-index: 1;
          opacity: 1;
        }

        /* Distant autumn forest hills */
        .autumn-hills {
          position: absolute;
          bottom: 35%;
          left: 0;
          width: 100%;
          height: 30%;
          background: linear-gradient(
            to bottom,
            rgba(140, 90, 50, 0.4) 0%,
            rgba(120, 75, 40, 0.6) 50%,
            rgba(100, 60, 30, 0.8) 100%
          );
          z-index: 2;
          opacity: 1;
        }

        /* Autumn trees - left frame */
        .autumn-frame-left {
          position: absolute;
          top: 0;
          left: 0;
          width: 30%;
          height: 65%;
          z-index: 3;
          opacity: 1;
        }

        /* Autumn trees - right frame */
        .autumn-frame-right {
          position: absolute;
          top: 0;
          right: 0;
          width: 30%;
          height: 65%;
          z-index: 3;
          opacity: 1;
        }

        .autumn-tree-trunk {
          position: absolute;
          bottom: 0;
          background: linear-gradient(
            90deg,
            #3D2817 0%,
            #5C4033 30%,
            #6B4D3B 50%,
            #5C4033 70%,
            #3D2817 100%
          );
          border-radius: 50% 50% 0 0;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.5);
        }

        .autumn-foliage-cluster {
          position: absolute;
          border-radius: 50%;
          animation: foliageSway 10s ease-in-out infinite;
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
          opacity: 1;
        }

        @keyframes foliageSway {
          0%, 100% { transform: translateY(0) rotate(-1deg); }
          50% { transform: translateY(-8px) rotate(1deg); }
        }

        /* Warm harvest table surface */
        .harvest-table-surface {
          position: absolute;
          bottom: 18%;
          left: 0;
          width: 100%;
          height: 30%;
          background: linear-gradient(
            135deg,
            #6B4423 0%,
            #8B5A3C 20%,
            #9B6A48 40%,
            #A57A54 50%,
            #9B6A48 60%,
            #8B5A3C 80%,
            #6B4423 100%
          );
          box-shadow: 
            0 -25px 60px rgba(0, 0, 0, 0.5),
            inset 0 -8px 25px rgba(0, 0, 0, 0.3),
            inset 0 8px 20px rgba(255, 255, 255, 0.1);
          z-index: 6;
          opacity: 1;
        }

        /* Wood grain texture */
        .wood-texture {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: repeating-linear-gradient(
            90deg,
            transparent 0px,
            rgba(0, 0, 0, 0.04) 1px,
            transparent 2px,
            transparent 18px
          );
          opacity: 0.5;
        }

        /* Beautiful harvest pumpkin */
        .harvest-pumpkin {
          position: absolute;
          border-radius: 50%;
          box-shadow: 
            0 15px 40px rgba(0, 0, 0, 0.5),
            inset -20px -20px 45px rgba(0, 0, 0, 0.35),
            inset 20px 20px 45px rgba(255, 230, 140, 0.4);
          animation: warmGlow 6s ease-in-out infinite;
          opacity: 1;
        }

        @keyframes warmGlow {
          0%, 100% { 
            filter: brightness(1);
          }
          50% { 
            filter: brightness(1.15);
          }
        }

        .pumpkin-stem {
          position: absolute;
          top: -22px;
          left: 50%;
          transform: translateX(-50%);
          width: 18px;
          height: 25px;
          background: linear-gradient(
            180deg,
            #8B7355 0%,
            #6B5638 100%
          );
          border-radius: 4px 4px 0 0;
          box-shadow: 
            0 3px 10px rgba(0, 0, 0, 0.5),
            inset -3px 0 8px rgba(0, 0, 0, 0.4);
        }

        .pumpkin-ridge {
          position: absolute;
          top: 0;
          height: 100%;
          width: 3px;
          background: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.15) 0%,
            rgba(0, 0, 0, 0.35) 50%,
            rgba(0, 0, 0, 0.15) 100%
          );
        }

        /* Warm candle with realistic flame */
        .warm-candle {
          position: absolute;
          z-index: 8;
          opacity: 1;
        }

        .candle-body {
          width: 20px;
          height: 70px;
          background: linear-gradient(
            90deg,
            #FFF8E7 0%,
            #FFFAEB 50%,
            #FFF8E7 100%
          );
          border-radius: 4px;
          box-shadow: 
            0 8px 25px rgba(0, 0, 0, 0.4),
            inset 0 3px 10px rgba(255, 255, 255, 0.6),
            inset 0 -3px 10px rgba(0, 0, 0, 0.15);
        }

        .candle-flame {
          position: absolute;
          top: -30px;
          left: 50%;
          transform: translateX(-50%);
          width: 16px;
          height: 34px;
          background: linear-gradient(
            to top,
            #FF6B35 0%,
            #FF8C42 15%,
            #FFA559 30%,
            #FFB84D 50%,
            #FFD580 75%,
            #FFF4E6 100%
          );
          border-radius: 50% 50% 25% 25%;
          filter: blur(2px);
          animation: flameFlicker 2.8s ease-in-out infinite;
          box-shadow: 
            0 0 30px rgba(255, 180, 80, 0.9),
            0 0 50px rgba(255, 140, 60, 0.6),
            0 0 70px rgba(255, 120, 40, 0.4);
        }

        @keyframes flameFlicker {
          0%, 100% { 
            transform: translateX(-50%) scaleY(1) scaleX(1); 
            opacity: 0.96;
          }
          20% { 
            transform: translateX(-50%) scaleY(1.14) scaleX(0.94); 
            opacity: 1;
          }
          40% { 
            transform: translateX(-50%) scaleY(0.94) scaleX(1.08); 
            opacity: 0.92;
          }
          60% { 
            transform: translateX(-50%) scaleY(1.08) scaleX(0.96); 
            opacity: 0.99;
          }
          80% { 
            transform: translateX(-50%) scaleY(0.98) scaleX(1.04); 
            opacity: 0.94;
          }
        }

        .flame-glow {
          position: absolute;
          top: -38px;
          left: 50%;
          transform: translateX(-50%);
          width: 75px;
          height: 75px;
          background: radial-gradient(
            circle,
            rgba(255, 200, 100, 0.7) 0%,
            rgba(255, 170, 80, 0.5) 30%,
            rgba(255, 150, 70, 0.3) 50%,
            transparent 75%
          );
          border-radius: 50%;
          filter: blur(15px);
          animation: glowPulse 2.8s ease-in-out infinite;
        }

        @keyframes glowPulse {
          0%, 100% { 
            opacity: 0.75; 
            transform: translateX(-50%) scale(1);
          }
          50% { 
            opacity: 1; 
            transform: translateX(-50%) scale(1.2);
          }
        }

        /* Wine bottle */
        .wine-bottle {
          position: absolute;
          bottom: 30%;
          left: 48%;
          width: 40px;
          height: 115px;
          background: linear-gradient(
            180deg,
            #3A1010 0%,
            #5C1818 25%,
            #6B2020 50%,
            #5C1818 75%,
            #3A1010 100%
          );
          border-radius: 8px 8px 4px 4px;
          box-shadow: 
            0 15px 40px rgba(0, 0, 0, 0.7),
            inset -8px 0 20px rgba(0, 0, 0, 0.6);
          z-index: 7;
          opacity: 1;
        }

        .wine-neck {
          position: absolute;
          top: -35px;
          left: 50%;
          transform: translateX(-50%);
          width: 16px;
          height: 40px;
          background: linear-gradient(
            180deg,
            #2A0808 0%,
            #3A1010 50%,
            #4A1414 100%
          );
          border-radius: 4px 4px 0 0;
          box-shadow: inset -3px 0 10px rgba(0, 0, 0, 0.8);
        }

        .wine-label {
          position: absolute;
          top: 42%;
          left: 50%;
          transform: translateX(-50%);
          width: 30px;
          height: 24px;
          background: linear-gradient(
            135deg,
            #F5DEB3 0%,
            #E8D4A8 50%,
            #DEB887 100%
          );
          border-radius: 2px;
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.4);
        }

        /* Realistic grape cluster */
        .grape-cluster {
          position: absolute;
          z-index: 7;
          opacity: 1;
        }

        .grape {
          position: absolute;
          width: 15px;
          height: 17px;
          background: radial-gradient(
            circle at 35% 35%,
            #A855F7 0%,
            #8B5CF6 30%,
            #7C3AED 60%,
            #6D28D9 100%
          );
          border-radius: 50% 50% 48% 52%;
          box-shadow: 
            0 3px 10px rgba(0, 0, 0, 0.5),
            inset -4px -4px 8px rgba(0, 0, 0, 0.5),
            inset 3px 3px 6px rgba(200, 150, 255, 0.4);
        }

        /* Falling autumn leaves */
        .falling-autumn-leaf {
          position: absolute;
          animation: leafFall linear infinite;
          filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.3));
          opacity: 1;
        }

        @keyframes leafFall {
          0% {
            transform: translateY(-50px) rotate(0deg) translateX(0);
            opacity: 0.85;
          }
          50% {
            transform: translateY(50vh) rotate(180deg) translateX(50px);
            opacity: 0.7;
          }
          100% {
            transform: translateY(105vh) rotate(360deg) translateX(-40px);
            opacity: 0.3;
          }
        }

        .leaf-maple {
          width: 100%;
          height: 100%;
          clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
        }

        .leaf-oak {
          width: 100%;
          height: 100%;
          border-radius: 50% 0 50% 50%;
        }

        /* Warm ground layer */
        .warm-ground {
          position: absolute;
          bottom: 0;
          width: 100%;
          height: 18%;
          background: linear-gradient(
            to bottom,
            rgba(90, 60, 40, 0.75) 0%,
            rgba(75, 50, 35, 0.9) 50%,
            rgba(60, 40, 28, 1) 100%
          );
          z-index: 9;
          box-shadow: 0 -20px 50px rgba(90, 60, 40, 0.5);
          opacity: 1;
        }

        .ground-leaf {
          position: absolute;
          width: 14px;
          height: 12px;
          border-radius: 50% 0 50% 0;
          opacity: 0.6;
          transform: rotate(var(--rotation));
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
        }

        /* Responsive design */
        @media (max-width: 768px) {
          .autumn-frame-left,
          .autumn-frame-right { width: 28%; }
          .harvest-table-surface { bottom: 20%; height: 28%; }
          .wine-bottle { width: 32px; height: 90px; }
          .harvest-pumpkin { transform: scale(0.8); }
          .warm-candle { transform: scale(0.85); }
        }
      `}</style>

      {/* Warm golden autumn sky */}
      <div className="warm-autumn-sky" />

      {/* Golden sunset glow */}
      <div className="golden-glow" />

      {/* Distant autumn hills */}
      <div className="autumn-hills" />

      {/* Left framing autumn trees */}
      <div className="autumn-frame-left">
        {[
          { x: 20, trunk: 45, foliage: [
            { x: 12, y: 280, size: 105, color: '#FF8C42' },
            { x: 65, y: 310, size: 95, color: '#FFA559' },
            { x: 38, y: 350, size: 88, color: '#FFB366' }
          ]},
          { x: 55, trunk: 40, foliage: [
            { x: 45, y: 300, size: 98, color: '#FF9E4F' },
            { x: 88, y: 335, size: 90, color: '#FFAD66' }
          ]}
        ].map((tree, idx) => (
          <div key={`left-${idx}`}>
            <div 
              className="autumn-tree-trunk"
              style={{
                left: `${tree.x}%`,
                width: `${tree.trunk}px`,
                height: '70%'
              }}
            />
            {tree.foliage.map((f, fIdx) => (
              <div
                key={fIdx}
                className="autumn-foliage-cluster"
                style={{
                  left: `${f.x}%`,
                  bottom: `${f.y}px`,
                  width: `${f.size}px`,
                  height: `${f.size}px`,
                  background: `radial-gradient(circle at 35% 35%, ${f.color}, ${f.color}dd, ${f.color}aa)`,
                  animationDelay: `${idx * 0.6 + fIdx * 0.3}s`
                }}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Right framing autumn trees */}
      <div className="autumn-frame-right">
        {[
          { x: 18, trunk: 42, foliage: [
            { x: 8, y: 290, size: 100, color: '#FF9E4F' },
            { x: 55, y: 320, size: 92, color: '#FFAD66' }
          ]},
          { x: 52, trunk: 38, foliage: [
            { x: 40, y: 310, size: 95, color: '#FF8C42' },
            { x: 82, y: 345, size: 85, color: '#FFA559' }
          ]}
        ].map((tree, idx) => (
          <div key={`right-${idx}`}>
            <div 
              className="autumn-tree-trunk"
              style={{
                right: `${tree.x}%`,
                width: `${tree.trunk}px`,
                height: '70%'
              }}
            />
            {tree.foliage.map((f, fIdx) => (
              <div
                key={fIdx}
                className="autumn-foliage-cluster"
                style={{
                  right: `${f.x}%`,
                  bottom: `${f.y}px`,
                  width: `${f.size}px`,
                  height: `${f.size}px`,
                  background: `radial-gradient(circle at 35% 35%, ${f.color}, ${f.color}dd, ${f.color}aa)`,
                  animationDelay: `${idx * 0.6 + fIdx * 0.3 + 0.2}s`
                }}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Warm harvest table */}
      <div className="harvest-table-surface">
        <div className="wood-texture" />
        
        {/* Beautiful pumpkins */}
        {[
          { x: '11%', bottom: '12%', size: 88, color: 'radial-gradient(circle at 35% 35%, #FFB347 0%, #FFA500 50%, #FF9500 100%)' },
          { x: '24%', bottom: '10%', size: 78, color: 'radial-gradient(circle at 35% 35%, #FF8C00 0%, #FF7700 50%, #FF6600 100%)' },
          { x: '76%', bottom: '11%', size: 82, color: 'radial-gradient(circle at 35% 35%, #FFA500 0%, #FF9000 50%, #FF8000 100%)' },
          { x: '87%', bottom: '9%', size: 72, color: 'radial-gradient(circle at 35% 35%, #FFB347 0%, #FFA500 50%, #FF9500 100%)' },
        ].map((pumpkin, i) => (
          <div
            key={i}
            className="harvest-pumpkin"
            style={{
              left: pumpkin.x,
              bottom: pumpkin.bottom,
              width: `${pumpkin.size}px`,
              height: `${pumpkin.size}px`,
              background: pumpkin.color,
              animationDelay: `${i * 0.8}s`,
            }}
          >
            <div className="pumpkin-stem" />
            {[20, 33, 50, 67, 80].map((pos) => (
              <div
                key={pos}
                className="pumpkin-ridge"
                style={{ left: `${pos}%` }}
              />
            ))}
          </div>
        ))}

        {/* Warm candles with flickering flames */}
        {[
          { x: '37%', bottom: '18%', delay: 0 },
          { x: '55%', bottom: '20%', delay: 1 },
          { x: '66%', bottom: '17%', delay: 2 },
        ].map((candle, i) => (
          <div
            key={i}
            className="warm-candle"
            style={{
              left: candle.x,
              bottom: candle.bottom,
            }}
          >
            <div className="flame-glow" style={{ animationDelay: `${candle.delay}s` }} />
            <div className="candle-flame" style={{ animationDelay: `${candle.delay}s` }} />
            <div className="candle-body" />
          </div>
        ))}

        {/* Wine bottle */}
        <div className="wine-bottle">
          <div className="wine-neck" />
          <div className="wine-label" />
        </div>

        {/* Grape clusters */}
        {[
          { x: '17%', bottom: '8%' },
          { x: '71%', bottom: '7%' },
        ].map((cluster, idx) => (
          <div
            key={idx}
            className="grape-cluster"
            style={{
              left: cluster.x,
              bottom: cluster.bottom,
            }}
          >
            {[
              { x: 0, y: 0 }, { x: 13, y: 1 }, { x: 26, y: 0 },
              { x: 6, y: 14 }, { x: 20, y: 15 },
              { x: 3, y: 28 }, { x: 16, y: 27 }, { x: 29, y: 28 },
            ].map((grape, i) => (
              <div
                key={i}
                className="grape"
                style={{
                  left: `${grape.x}px`,
                  bottom: `${grape.y}px`,
                }}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Falling autumn leaves */}
      {fallingLeaves.map((leaf) => (
        <div
          key={leaf.id}
          className="falling-autumn-leaf"
          style={{
            left: `${leaf.left}%`,
            top: '-50px',
            width: `${leaf.size}px`,
            height: `${leaf.size}px`,
            animationDuration: `${leaf.animationDuration}s`,
            animationDelay: `${leaf.animationDelay}s`,
            filter: `blur(${leaf.blur}px) drop-shadow(0 3px 6px rgba(0, 0, 0, 0.3))`,
            opacity: leaf.opacity,
          }}
        >
          <div
            className={leaf.leafType % 2 === 0 ? 'leaf-maple' : 'leaf-oak'}
            style={{
              background: [
                'linear-gradient(135deg, #FF6B35 0%, #FF4500 100%)',
                'linear-gradient(135deg, #FFA500 0%, #FF8C00 100%)',
                'linear-gradient(135deg, #FFD700 0%, #FFB347 100%)',
                'linear-gradient(135deg, #FF8C42 0%, #FF7733 100%)',
              ][leaf.leafType],
              transform: `rotate(${leaf.rotation}deg)`,
            }}
          />
        </div>
      ))}

      {/* Warm ground with scattered leaves */}
      <div className="warm-ground">
        {Array.from({ length: 32 }, (_, i) => (
          <div
            key={i}
            className="ground-leaf"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 70}%`,
              '--rotation': `${Math.random() * 360}deg`,
              background: [
                'linear-gradient(135deg, #FF6B35 0%, #FF4500 100%)',
                'linear-gradient(135deg, #FFA500 0%, #FF8C00 100%)',
                'linear-gradient(135deg, #FFD700 0%, #FFB347 100%)',
                'linear-gradient(135deg, #FF8C42 0%, #FF7733 100%)',
              ][i % 4],
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default ThanksgivingSceneOverlay;
